
import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "./use-toast";
import { useAuth } from "@/context/AuthContext";
import { checkDeadlines } from "@/utils/notificationUtils";

type Notification = {
  id: string;
  title: string;
  message: string;
  is_read: boolean;
  type: string;
  created_at: string;
};

// Query key factory for notifications
const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (userId: string | undefined) => [...notificationKeys.lists(), { userId }] as const,
};

// Fetch notifications function
const fetchNotifications = async (userId: string | undefined): Promise<Notification[]> => {
  if (!userId) return [];
  
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  
  return data as Notification[];
};

// Mark notification as read function
const markAsRead = async (params: { id: string, userId: string }): Promise<void> => {
  const { id, userId } = params;
  
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', id)
    .eq('user_id', userId);
  
  if (error) throw error;
};

// Mark all notifications as read function
const markAllAsRead = async (userId: string): Promise<void> => {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('user_id', userId)
    .eq('is_read', false);
  
  if (error) throw error;
};

export const useNotifications = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;
  
  // Get notifications with React Query
  const { 
    data = [], 
    isLoading,
    refetch
  } = useQuery({
    queryKey: notificationKeys.list(userId),
    queryFn: () => fetchNotifications(userId),
    enabled: !!userId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
  
  // Calculate unread count
  const unreadCount = data.filter(n => !n.is_read).length;
  
  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => markAsRead({ id, userId: userId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.list(userId) });
    },
    onError: (error: any) => {
      console.error("Error marking notification as read:", error);
      toast({
        title: "Failed to update notification",
        description: error.message,
        variant: "destructive"
      });
    }
  });
  
  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () => markAllAsRead(userId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.list(userId) });
    },
    onError: (error: any) => {
      console.error("Error marking all notifications as read:", error);
      toast({
        title: "Failed to update notifications",
        description: error.message,
        variant: "destructive"
      });
    }
  });
  
  // Check for approaching deadlines
  useEffect(() => {
    // Only run if user is logged in
    if (!userId) return;
    
    // Check deadlines on initial load
    checkDeadlines();
    
    // Set up interval to check for deadlines periodically (every hour)
    const checkInterval = setInterval(() => {
      checkDeadlines();
    }, 60 * 60 * 1000); // Every hour
    
    return () => {
      clearInterval(checkInterval);
    };
  }, [userId]);
  
  // Set up real-time subscription for notifications
  useEffect(() => {
    if (!userId) return;
    
    // Subscribe to changes in the notifications table
    const channel = supabase
      .channel('notifications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        () => {
          refetch();
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, refetch]);
  
  return {
    notifications: data,
    unreadCount,
    isLoading,
    markAsRead: (id: string) => markAsReadMutation.mutate(id),
    markAllAsRead: () => markAllAsReadMutation.mutate(),
    refresh: refetch,
    checkDeadlines
  };
};
